import { useState } from "react";
import { Clock } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { DAYS, TIMETABLE, slotLabel } from "@/constants/timetable";
import { useBooking } from "@/components/booking/BookingDialog";

// Plan your week: the class timetable. Tap any session to request it.
export function PlanYourWeek() {
  const { open } = useBooking();
  const today = DAYS[(new Date().getDay() + 6) % 7];
  const [day, setDay] = useState(today);
  return (
    <section id="timetable" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-32 scroll-mt-24" data-testid="plan-your-week-section">
      <Reveal className="max-w-2xl">
        <p className="eyebrow">Plan your week</p>
        <h2 className="font-anton uppercase tracking-tight text-cream text-4xl sm:text-6xl mt-3 leading-[0.95]">Training that fits real life.</h2>
        <p className="text-smoke mt-5 text-base sm:text-lg">Coached sessions across the week. Find a routine that fits your lifestyle.</p>
      </Reveal>

      <Reveal delay={0.08}>
        <div className="mt-12 border border-gold/40 bg-surface">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-2 px-5 sm:px-8 py-6 border-b border-gold/30">
            <div>
              <p className="font-anton uppercase text-gold text-xl sm:text-2xl tracking-wide leading-none">Elysium Training Club</p>
              <p className="font-anton uppercase text-cream text-3xl sm:text-4xl tracking-wide leading-none mt-1">Class Timetable</p>
            </div>
            <p className="text-smoke text-sm">Tap a session to book it.</p>
          </div>

          {/* Phone: one day at a time */}
          <div className="lg:hidden">
            <div className="flex overflow-x-auto border-b border-line" role="tablist">
              {DAYS.map((d) => (
                <button key={d} role="tab" aria-selected={d === day} onClick={() => setDay(d)}
                  className={`shrink-0 min-w-[64px] h-12 px-3 font-anton uppercase tracking-wide text-base ${d === day ? "bg-gold text-void" : "text-cream hover:text-gold"}`}
                  data-testid={`timetable-day-${d}`}>{d.slice(0, 3)}</button>
              ))}
            </div>
            <ul className="divide-y divide-line">
              {TIMETABLE[day].map((s) => (
                <li key={s.time + s.name}>
                  <button onClick={() => open(slotLabel(day, s))} className="w-full flex items-center gap-4 px-5 py-4 text-left hover:bg-void/60 transition-colors" data-testid={`timetable-slot-${day}-${s.time}`}>
                    <span className="font-anton text-gold text-2xl w-16 shrink-0">{s.time}</span>
                    <span className="flex-1 min-w-0">
                      <span className="block text-cream font-semibold">{s.name}</span>
                      <span className="block text-smoke text-sm">Coach {s.coach}</span>
                    </span>
                    <span className="text-gold text-xs uppercase tracking-wider font-semibold shrink-0">Book</span>
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Desktop: the whole week */}
          <div className="hidden lg:grid grid-cols-7 divide-x divide-line">
            {DAYS.map((d) => (
              <div key={d} className="min-w-0">
                <p className={`font-anton uppercase tracking-wide text-center py-3 border-b border-line ${d === today ? "text-gold" : "text-cream"}`}>{d}</p>
                <div className="p-2 space-y-2">
                  {TIMETABLE[d].map((s) => (
                    <button key={s.time + s.name} onClick={() => open(slotLabel(d, s))}
                      className="w-full text-left p-3 border border-line hover:border-gold bg-void/40 hover:bg-void transition-colors group" data-testid={`timetable-cell-${d}-${s.time}`}>
                      <span className="flex items-center gap-1.5 font-anton text-gold text-lg"><Clock className="h-3.5 w-3.5" />{s.time}</span>
                      <span className="block text-cream text-sm font-semibold mt-1 leading-tight">{s.name}</span>
                      <span className="block text-smoke text-[13px] mt-0.5 leading-tight">{s.coach}</span>
                      <span className="block text-gold text-xs uppercase tracking-wider mt-2 opacity-0 group-hover:opacity-100 transition-opacity">Book</span>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="px-5 sm:px-8 py-6 border-t border-gold/30 flex justify-center">
            <button onClick={() => open()} className="h-14 px-10 bg-gold text-void hover:bg-gold-hover font-bold uppercase tracking-wider w-full sm:w-auto" data-testid="timetable-book-button">
              Book Coached Session
            </button>
          </div>
        </div>
      </Reveal>
    </section>
  );
}
